import React from 'react';
import { partition } from 'lodash';
import EventTracking from 'lib/EventTracking';

import CenteredContainer from 'components/CenteredContainer';
import { ComposableTable, getTextCellGenerator } from 'components/ComposableTable';
import Container from 'components/Container';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingCoordinatorParticipantSidebarView from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorParticipantSidebarView';
import ErrorReporter from 'app/components/ErrorReporter';
import Heading from 'components/Heading';
import GetCoachingSessionInstancesWithDistanceLearningMetadataContainer, {
  CoachingSessionInstanceWithDistanceLearningMetadata,
} from 'coaching/containers/GetCoachingSessionInstancesWithDistanceLearningMetadataContainer';
import LinkConstants from 'constants/LinkConstants';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import { ParticipantRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import { useCoachingViewState } from 'coaching/components/CoachingView';
import CoachingConstants from 'coaching/CoachingConstants';

export default function CoachingCoordinatorNoShowsView() {
  const [selectedParticipant, setSelectedParticipant] = React.useState<ParticipantRow>();
  const { closeSidebar, setSidebar } = useCoachingViewState();

  const eventPrefix = `${CoachingConstants.COORDINATOR_TOOL_EVENT_PREFIX}.sessions.no_shows`;
  function trackEvent(event: string) {
    EventTracking.trackKey(`${eventPrefix}.${event}`);
  }

  React.useEffect(() => {
    trackEvent('view');
    return function cleanup() {
      closeSidebar();
    };
  }, []);

  function coachColumnHeaders() {
    return [{ name: 'Name', width: 150 }, { name: '' }, { name: 'Student name' }];
  }

  function coachColumnGenerators(): Array<
    (data: CoachingSessionInstanceWithDistanceLearningMetadata) => React.ReactNode
  > {
    return [
      getTextCellGenerator(instance => instance.coach.name),
      getTextCellGenerator(instance => (
        <Link
          target="_blank"
          href={`${LinkConstants.R101_ADMIN_USER_BY_UUID_URL}${instance.coach.uuid}`}
          onClick={() => trackEvent('coach_absent_view_r101.click')}>
          View in R101
        </Link>
      )),
      getTextCellGenerator(instance => instance.participants.map(p => p.studentName)),
    ];
  }

  function participantColumnHeaders() {
    return [{ name: 'Name', width: 150 }, { name: '' }];
  }

  function participantColumnGenerators(): Array<
    (data: CoachingSessionInstanceWithDistanceLearningMetadata) => React.ReactNode
  > {
    return [
      getTextCellGenerator(instance => instance.participants.map(p => p.studentName)),
      getTextCellGenerator(instance =>
        instance.participants.map(p => (
          <Link
            target="_blank"
            key={p.uuid}
            href={`${LinkConstants.R101_ADMIN_USER_BY_UUID_URL}${p.studentUuid || p.purchaserUuid}`}
            onClick={() => trackEvent('student_absent_view_r101.click')}>
            View in R101
          </Link>
        ))
      ),
    ];
  }

  function partitionInstancesByStatus(
    instances: CoachingSessionInstanceWithDistanceLearningMetadata[]
  ) {
    const [lateCoachInstances, potentiallyLateParticipantInstances] = partition(
      instances,
      instance => instance.coachIsLate
    );
    const [lateParticipantInstances, allOtherInstances] = partition(
      potentiallyLateParticipantInstances,
      instance => instance.participantIsLate
    );
    return {
      lateCoachInstances,
      lateParticipantInstances,
      allOtherInstances,
    };
  }

  const handleSelectParticipant = (selectedParticipant: ParticipantRow) => {
    trackEvent('select_student.click');
    setSelectedParticipant(selectedParticipant);
    setSidebar(<CoachingCoordinatorParticipantSidebarView participant={selectedParticipant} />);
  };

  function renderHeader(title: string, numberOfSessions: number) {
    return (
      <Row size="small">
        <MediaObject center>
          <Heading level="2" flush>
            <Text weight="semibold">{title}</Text>
          </Heading>
          <Text size="medium">{numberOfSessions}</Text>
        </MediaObject>
      </Row>
    );
  }

  const findMatchingRow = (instances: CoachingSessionInstanceWithDistanceLearningMetadata[]) => {
    const matchingRowKey = instances.find(i =>
      i.participants.some(p => p.uuid === selectedParticipant?.uuid)
    )?.coachingSessionUuid;
    return matchingRowKey ? [matchingRowKey] : [];
  };
  return (
    <GetCoachingSessionInstancesWithDistanceLearningMetadataContainer
      hoursToLookBack={1}
      hoursToLookAhead={1}>
      {({ instances, loading, error }) => {
        if (loading && !instances) {
          return <CenteredLoadingIndicator />;
        }
        if (error || !instances)
          return (
            <ErrorReporter error="CoachingCoordinatorNoShowsView - could not fetch coaching sessions">
              <CenteredContainer size="medium">
                <Text align="center" size="large" font="serif">
                  Oops! We couldn’t load the coaching sessions. Please refresh and try again. If
                  this issue persists, please notify an engineer.
                </Text>
              </CenteredContainer>
            </ErrorReporter>
          );
        const { lateCoachInstances, lateParticipantInstances } = partitionInstancesByStatus(
          instances
        );
        return (
          <Container size="large">
            <Row>
              {renderHeader('Coach absent', lateCoachInstances.length)}
              <ComposableTable<CoachingSessionInstanceWithDistanceLearningMetadata>
                rows={lateCoachInstances}
                columnHeaders={coachColumnHeaders()}
                columnGenerators={coachColumnGenerators()}
                fixedHeader
                keyField="coachingSessionUuid"
                keysOfActiveRows={findMatchingRow(lateCoachInstances)}
                handleRowClick={row => handleSelectParticipant(row.participants[0])}
              />
            </Row>
            <Row>
              {renderHeader('Student absent', lateParticipantInstances.length)}
              <ComposableTable<CoachingSessionInstanceWithDistanceLearningMetadata>
                rows={lateParticipantInstances}
                columnHeaders={participantColumnHeaders()}
                columnGenerators={participantColumnGenerators()}
                fixedHeader
                keyField="coachingSessionUuid"
                keysOfActiveRows={findMatchingRow(lateParticipantInstances)}
                handleRowClick={row => handleSelectParticipant(row.participants[0])}
              />
            </Row>
          </Container>
        );
      }}
    </GetCoachingSessionInstancesWithDistanceLearningMetadataContainer>
  );
}
